/* Free Gift Cart Component Script */

class FreeGiftCart {
  constructor(element) {
    this.element = element;
    this.giftProductId = element.dataset.giftProductId;
    this.giftThreshold = parseInt(element.dataset.giftThreshold);
    
    this.offerContainer = element.querySelector('[data-free-gift-offer]');
    this.lockedState = element.querySelector('[data-free-gift-locked]');
    this.unlockedState = element.querySelector('[data-free-gift-unlocked]');
    this.confirmationState = element.querySelector('[data-free-gift-confirmation]');
    this.claimButton = element.querySelector('[data-free-gift-claim]');
    this.variantSelect = element.querySelector('[data-free-gift-variant]');
    this.amountRemainingElement = element.querySelector('[data-amount-remaining]');
    
    this.init();
  }

  init() {
    // Bind event listeners
    if (this.claimButton) {
      this.claimButton.addEventListener('click', () => this.handleClaimClick());
    }

    // Listen for cart updates
    document.addEventListener('on:cart:after-merge', () => this.updateState());
    document.addEventListener('cart:updated', () => this.updateState());
    
    // Initial state update
    this.updateState();
  }

  async updateState() {
    try {
      const cart = await this.fetchCart();
      const cartTotal = cart.total_price;
      const amountRemaining = this.giftThreshold - cartTotal;
      const isUnlocked = cartTotal >= this.giftThreshold;
      const giftInCart = this.isGiftInCart(cart);

      // Update the amount remaining display
      if (this.amountRemainingElement && amountRemaining > 0) {
        this.amountRemainingElement.textContent = this.formatMoney(amountRemaining);
      }

      // Update state visibility
      if (giftInCart) {
        // Show confirmation, hide offer
        this.showConfirmation();
      } else if (isUnlocked) {
        // Show unlocked state
        this.showUnlocked();
      } else {
        // Show locked state
        this.showLocked();
      }
    } catch (error) {
      console.error('Error updating free gift state:', error);
    }
  }

  showLocked() {
    if (this.lockedState) this.lockedState.classList.remove('hidden');
    if (this.unlockedState) this.unlockedState.classList.add('hidden');
    if (this.offerContainer) this.offerContainer.classList.remove('hidden');
    if (this.confirmationState) this.confirmationState.classList.add('hidden');
    if (this.claimButton) {
      this.claimButton.classList.add('free-gift-cart__claim-btn--disabled');
      this.claimButton.disabled = true;
    }
  }

  showUnlocked() {
    if (this.lockedState) this.lockedState.classList.add('hidden');
    if (this.unlockedState) this.unlockedState.classList.remove('hidden');
    if (this.offerContainer) this.offerContainer.classList.remove('hidden');
    if (this.confirmationState) this.confirmationState.classList.add('hidden');
    if (this.claimButton) {
      this.claimButton.classList.remove('free-gift-cart__claim-btn--disabled');
      this.claimButton.disabled = false;
    }
  }

  showConfirmation() {
    if (this.offerContainer) this.offerContainer.classList.add('hidden');
    if (this.confirmationState) this.confirmationState.classList.remove('hidden');
  }

  async handleClaimClick() {
    if (!this.variantSelect) return;

    const variantId = this.variantSelect.value;
    if (!variantId) {
      alert('Please select a variant');
      return;
    }

    // Disable button to prevent double clicks
    this.claimButton.disabled = true;
    this.claimButton.textContent = 'ADDING...';

    try {
      await this.addToCart(variantId);
      
      // Show confirmation immediately
      this.showConfirmation();
      
      // Trigger cart refresh
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
      
      // Reset button text
      this.claimButton.textContent = 'CLAIM';
    } catch (error) {
      console.error('Error adding free gift to cart:', error);
      alert('Sorry, there was an error adding the gift to your cart. Please try again.');
      
      // Re-enable button
      this.claimButton.disabled = false;
      this.claimButton.textContent = 'CLAIM';
    }
  }

  async addToCart(variantId) {
    const response = await fetch(window.theme.routes.cartAdd, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items: [
          {
            id: variantId,
            quantity: 1,
            properties: {
              '_free_gift': 'true'
            }
          }
        ]
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.description || 'Failed to add to cart');
    }

    return await response.json();
  }

  async fetchCart() {
    const response = await fetch('/cart.js');
    if (!response.ok) {
      throw new Error('Failed to fetch cart');
    }
    return await response.json();
  }

  isGiftInCart(cart) {
    return cart.items.some(item => 
      item.product_id.toString() === this.giftProductId.toString()
    );
  }

  formatMoney(cents) {
    // Use Shopify's money format if available
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, window.theme.settings.moneyWithCurrencyFormat);
    }
    
    // Fallback formatting
    const dollars = (cents / 100).toFixed(2);
    return `€${dollars}`;
  }
}

// Initialize all free gift cart components
function initFreeGiftCarts() {
  document.querySelectorAll('.free-gift-cart').forEach(element => {
    if (!element.freeGiftCartInstance) {
      element.freeGiftCartInstance = new FreeGiftCart(element);
    }
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initFreeGiftCarts);
} else {
  initFreeGiftCarts();
}

// Re-initialize when cart drawer content is updated
document.addEventListener('on:cart:after-merge', () => {
  setTimeout(initFreeGiftCarts, 100);
});

